import { Navigate, Route, Routes } from 'react-router-dom';

import { RequireAuth } from './components/RequireAuth';
import { AdminHubPage } from './features/admin/hub/AdminHubPage';
import { AdminShell } from './features/admin/hub/AdminShell';
import { AgendaLembretesPage } from './features/admin/hub/domains/AgendaLembretesPage';
import { AtendimentoFilaPage } from './features/admin/hub/domains/AtendimentoFilaPage';
import { ContaPlanoPage } from './features/admin/hub/domains/ContaPlanoPage';
import { IaCondutaPage } from './features/admin/hub/domains/IaCondutaPage';
import { OrganizacaoPage } from './features/admin/hub/domains/OrganizacaoPage';
import { ferramentaRoutes } from './features/admin/hub/ferramenta-pages';
import { MensagensPage } from './features/direct-messages/MensagensPage';
import { configuracaoRedirects } from './features/navigation/configuracao-redirects';
import { useAppearanceEffect } from './hooks/use-appearance';
import { useCapabilities } from './hooks/use-capability';
import { AppShell } from './layouts/AppShell';
import { AceitarConvitePage } from './pages/AceitarConvite';
import { AcompanhamentoPage } from './pages/AcompanhamentoPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AgendaPage } from './pages/AgendaPage';
import { AnonimizacaoPage } from './pages/AnonimizacaoPage';
import { AprovacaoOverridesPage } from './pages/AprovacaoOverridesPage';
import { AuditExplorerPage } from './pages/AuditExplorerPage';
import { AuxiliarPage } from './pages/AuxiliarPage';
import { CampanhasPage } from './pages/CampanhasPage';
import { CentroAlertasPage } from './pages/CentroAlertasPage';
import { CentroDireitosPage } from './pages/CentroDireitosPage';
import { ColetaEstruturadaPage } from './pages/ColetaEstruturadaPage';
import { ConformidadePage } from './pages/ConformidadePage';
import { ConversationViewPage } from './pages/ConversationView';
import { DashboardQualidadePage } from './pages/DashboardQualidadePage';
import { EsqueciSenhaPage } from './pages/EsqueciSenha';
import { GuardianshipPage } from './pages/GuardianshipPage';
import { InboxPage } from './pages/InboxPage';
import { InboxTimePage } from './pages/InboxTimePage';
import { IniciarConversaPage } from './pages/IniciarConversaPage';
import { InsightsGestorPage } from './pages/InsightsGestorPage';
import { InsightsPage } from './pages/InsightsPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { IntervencaoPage } from './pages/IntervencaoPage';
import { LoginPage } from './pages/Login';
import { PainelP1Page } from './pages/PainelP1Page';
import { PainelPessoalPage } from './pages/PainelPessoalPage';
import { PainelSlaPage } from './pages/PainelSlaPage';
import { PendenciasPage } from './pages/PendenciasPage';
import { PessoaDetailPage } from './pages/PessoaDetailPage';
import { PessoasPage } from './pages/PessoasPage';
import { QualidadeIaPage } from './pages/QualidadeIaPage';
import { RedefinirSenhaPage } from './pages/RedefinirSenha';
import { RelatoriosLgpdPage } from './pages/RelatoriosLgpdPage';
import { RespostasProntasSupervisorPage } from './pages/RespostasProntasSupervisorPage';
import { SupervisorPanelPage } from './pages/SupervisorPanelPage';

/**
 * Landing pós-login por papel (Item 364). O operador cai no "home" do seu papel:
 * supervisor/gestor → Painel da Equipe; atendente → Painel Pessoal; DPO → Centro de
 * Direitos; fallback core → Caixa de entrada (sempre acessível). As capabilities já
 * estão carregadas aqui — RequireAuth só renderiza os filhos após o /v1/auth/me.
 */
function RootRedirect(): JSX.Element {
  const capabilities = useCapabilities();
  const has = (code: string): boolean => capabilities.includes(code);
  const to = has('supervisor-dashboard-read')
    ? '/supervisor'
    : has('agent-dashboard-read')
      ? '/painel'
      : has('lgpd-requests-read')
        ? '/dpo/centro-direitos'
        : '/inbox';
  return <Navigate to={to} replace />;
}

/**
 * Bloco de rotas do hub de Administração (spec 2026-08-22 — plano T1-T14, Task 3).
 *
 * Extraído como função exportável porque `AdminShell.spec.tsx` precisa montar as
 * rotas REAIS de `/admin` (índice + domínios + redirects) sem montar RequireAuth
 * + AppShell + Login — pesado demais para um teste de shell. Shell 4a; páginas de
 * domínio chegam em T7-T11 (`DomainPlaceholder` foi usado até lá — Organização
 * migrou na T7, IA & conduta na T8, Atendimento & fila na T9, Agenda & lembretes
 * na T10, Conta & plano na T11 — nenhuma rota real usa mais `DomainPlaceholder`;
 * o arquivo em si fica como residual para limpeza da T14).
 *
 * D5 (30/Set/2026): as 13 ferramentas (ex-/configuracao/*) montam AQUI DENTRO, em
 * `<dominio>/<id>`, geradas do catálogo por `ferramentaRoutes()` — nunca à mão.
 */
export function adminRoutes(): JSX.Element {
  return (
    <>
      <Route index element={<AdminHubPage />} />
      <Route path="organizacao" element={<OrganizacaoPage />} />
      <Route path="ia" element={<IaCondutaPage />} />
      <Route path="atendimento" element={<AtendimentoFilaPage />} />
      <Route path="agenda" element={<AgendaLembretesPage />} />
      <Route path="integracoes" element={<IntegrationsPage />} />
      <Route path="conta" element={<ContaPlanoPage />} />
      {/* Pontes de gestão continuam páginas próprias */}
      <Route path="users" element={<AdminUsersPage />} />
      <Route path="audit" element={<AuditExplorerPage />} />
      {/* D5 — as 13 ferramentas, dentro do shell, lidas do catálogo. */}
      {ferramentaRoutes()}
      {/* Redirects — nada quebra (spec §3.3). Âncora viaja no hash. */}
      <Route path="config" element={<Navigate to="/admin" replace />} />
      <Route path="ai-config" element={<Navigate to="/admin/ia#modelos" replace />} />
      <Route
        path="branding"
        element={<Navigate to="/admin/organizacao#identidade-visual" replace />}
      />
      <Route path="tenant-lifecycle" element={<Navigate to="/admin/conta#lifecycle" replace />} />
      <Route path="integrations" element={<Navigate to="/admin/integracoes" replace />} />
    </>
  );
}

export function App(): JSX.Element {
  // Aparência do operador (tema + densidade) — aplicada no `<html>` ANTES de
  // qualquer rota, inclusive `/login`: quem escolheu escuro não pode levar uma
  // tela branca na cara toda vez que a sessão expira.
  useAppearanceEffect();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Aceite de convite (04/Set/2026) — pública, irmã do /login: o convidado ainda não tem conta. */}
      <Route path="/convite/:secret" element={<AceitarConvitePage />} />
      {/* «Esqueci minha senha» (Item 477) — públicas. */}
      <Route path="/esqueci-senha" element={<EsqueciSenhaPage />} />
      <Route path="/redefinir-senha/:secret" element={<RedefinirSenhaPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Item 364 — landing por papel; '/' não é mais a listagem legada de conversas. */}
        <Route index element={<RootRedirect />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="atendimento/conversation/:id" element={<ConversationViewPage />} />
        {/* Brief 2B T4 — Coleta Estruturada (ADR-144) — painel em-conversa */}
        <Route
          path="atendimento/conversation/:conversationId/coleta"
          element={<ColetaEstruturadaPage />}
        />
        {/* Brief 2B T2 — Pessoas (visão consolidada) */}
        <Route path="pessoas" element={<PessoasPage />} />
        <Route path="pessoas/:personId" element={<PessoaDetailPage />} />
        {/* Brief 2B T8 — Iniciar Conversa Proativa (ADR-127) */}
        <Route path="iniciar-conversa" element={<IniciarConversaPage />} />
        {/* F4-b — Campanhas (Raiz de Tarefa): 1 tarefa-mãe → N conversas irmãs */}
        <Route path="campanhas" element={<CampanhasPage />} />
        {/* Brief 2B T3 — Pendências (caixa do atendente · ADR-109) */}
        <Route path="pendencias" element={<PendenciasPage />} />
        {/* Brief 2B T7 — Agenda/Calendar (ADR-115) */}
        <Route path="agenda" element={<AgendaPage />} />
        {/* Brief 2B T5 — Painel Pessoal (B5 · cockpit diário) */}
        <Route path="painel" element={<PainelPessoalPage />} />
        {/* Brief 2B T6 — Auxiliar (utilidades · fecha o Brief 2B FE 8/8) */}
        <Route path="auxiliar" element={<AuxiliarPage />} />
        {/* Mensageria interna entre operadores (Item #8b) — gate na própria página */}
        <Route path="mensagens" element={<MensagensPage />} />
        <Route path="mensagens/:threadId" element={<MensagensPage />} />
        {/* Brief 3 T1 — Painel da Equipe (Sprint Frontend Desktop W1.3) */}
        <Route path="supervisor" element={<SupervisorPanelPage />} />
        {/* Brief 3 T2 — Acompanhamento Silencioso (Sprint Frontend Brief 3 V2) */}
        <Route path="supervisor/acompanhamento" element={<AcompanhamentoPage />} />
        <Route path="supervisor/acompanhamento/:conversationId" element={<AcompanhamentoPage />} />
        {/* Brief 3 T3 — Intervenção (Sprint Frontend Brief 3 V2) */}
        <Route path="supervisor/intervencao/:conversationId" element={<IntervencaoPage />} />
        {/* Brief 3 T7 — Centro de Alertas (Sprint Frontend Brief 3 V2) */}
        <Route path="supervisor/alertas" element={<CentroAlertasPage />} />
        {/* Brief 3 T4 — Inbox do Time (Sprint Frontend Brief 3 V2) */}
        <Route path="supervisor/inbox" element={<InboxTimePage />} />
        {/* Brief 3 T5 — Painel de SLA (Sprint Frontend Brief 3 V2) */}
        <Route path="supervisor/sla" element={<PainelSlaPage />} />
        {/* Brief 3 T6 — Dashboard de Qualidade (Sprint Frontend Brief 3 V2) */}
        <Route path="supervisor/qualidade" element={<DashboardQualidadePage />} />
        {/* Respostas Prontas S4 — supervisor (uso por operador + alerta de conduta) */}
        <Route path="supervisor/respostas-prontas" element={<RespostasProntasSupervisorPage />} />
        {/* Brief 3 T8 — Painel de Gestão de P1 (Sprint Frontend Brief 3 V2) */}
        <Route path="supervisor/p1" element={<PainelP1Page />} />
        {/* Brief 3 T9 — Aprovação de Overrides (Sprint Frontend Brief 3 V2) */}
        <Route path="supervisor/overrides" element={<AprovacaoOverridesPage />} />
        {/* Brief 3 T10 — Insights Estratégicos (Sprint Frontend Brief 3 V2 — a última tela) */}
        <Route path="supervisor/insights" element={<InsightsPage />} />
        {/* Brief 5 B1 — Centro Direitos do Titular / DPO (Sprint Frontend Desktop W3.2) */}
        <Route path="dpo/centro-direitos" element={<CentroDireitosPage />} />
        {/* Brief 5 B4 — Relatórios LGPD / DPO (Sprint Frontend Brief 5 · Tela 1) */}
        <Route path="dpo/relatorios" element={<RelatoriosLgpdPage />} />
        {/* Brief 5 B5 — Anonimização / DPO (Sprint Frontend Brief 5 · Tela 3) */}
        <Route path="dpo/anonimizacao" element={<AnonimizacaoPage />} />
        {/* Brief 5 B6 — Guardianship / Curadoria / DPO (Sprint Frontend Brief 5 · Tela 2) */}
        <Route path="dpo/guardianship" element={<GuardianshipPage />} />
        {/* Brief 5 B3 — Centro de conformidade / DPO (lente forense de ConflictAlert — varredura #3) */}
        <Route path="dpo/conformidade" element={<ConformidadePage />} />
        {/* Admin hub por domínios (spec 2026-08-22). Shell 4a; páginas de domínio T7–T11. */}
        <Route path="admin" element={<AdminShell />}>
          {adminRoutes()}
        </Route>
        {/* D5 (30/Set/2026) — /configuracao/* morreu: 13 páginas viraram ferramenta no /admin,
            2 eram observação. Cada rota velha redireciona (hash viaja). */}
        {configuracaoRedirects()}
        {/* §5 da spec de 17/Set (D5): Qualidade IA é observação — vive em Supervisão. */}
        <Route path="supervisor/qualidade-ia" element={<QualidadeIaPage />} />
        {/* §5 da spec de 17/Set (D5): lente gestor da tela de insights — vive em Estratégia. */}
        <Route path="estrategia/insights-gestor" element={<InsightsGestorPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
